'use client'

import * as React from 'react'
import { ArrowLeft } from 'lucide-react'
import { cn } from '../../utils'

export interface BackButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visible label next to the arrow. */
  label?: string
  /**
   * What to do when clicked. Apps wire this to their router (e.g.
   * `() => router.back()`). Falls back to `history.back()` if omitted.
   */
  onBack?: () => void
}

/**
 * A simple "← Back" affordance. Framework-agnostic — pass `onBack` from
 * your router. Defaults to `window.history.back()` so it still works in
 * isolation.
 */
const BackButton = React.forwardRef<HTMLButtonElement, BackButtonProps>(
  ({ label = 'Back', onBack, className, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented) return
        if (onBack) {
          onBack()
        } else if (typeof window !== 'undefined') {
          window.history.back()
        }
      }}
      className={cn(
        'inline-flex items-center gap-2 text-sm text-subtext hover:text-text transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md',
        className
      )}
      {...props}
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </button>
  )
)
BackButton.displayName = 'BackButton'

export { BackButton }
